import React, { useReducer, useState } from 'react';
import { without, get, flatten } from 'lodash-es';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import * as FjPrimitives from 'components/primitives';
import { CloudErrors, LicenseUtils } from '@portal/portal-core';
import { AdminClientActions, AdminClientSelectors } from 'ducks/admin-client';
import styled from 'styled-components';
import type { RootState } from 'reducers';

import * as Utils from 'utils';
import { validateNoDuplicates } from 'utils/validators';
import { CloudAdminActions } from 'actions';
import {
  selectAvailableSeats,
  currentProductAccessSelector,
  allUsersSelector,
  allInvitesSelector,
} from 'selectors';
import { AlertMessage, Modal } from 'components/styled-primitives';
import { validateEmail } from './email-validation';

// Minimal shapes for the redux slices this component reads.
interface CostCenter {
  id?: unknown;
  whitelistDomains?: unknown;
  whitelistIgnore?: unknown;
}

// `selectAvailableSeats` returns `SeatUsage | {}`; the empty-object case only
// occurs when no license matches, and `.openSeats` reads back as `undefined`
// (preserving the original `undefined > 0` === false behavior).
interface SeatUsage {
  openSeats: number;
}

const InviteFailuresList = styled(FjPrimitives.FjTextArea).attrs({
  disabled: true,
})`
  flex: 1;
  border: 0;
  font-size: inherit;
  color: inherit;
  text-align: inherit;
  background: none;
`;

const getPlaceholder = (): string => 'Enter email addresses.\nSeparate by commas or a new line.';

const NewUsers = (): React.ReactElement => {
  const userAdminState = useSelector((state: RootState) => state.userAdmin);
  const seats = useSelector(selectAvailableSeats) as SeatUsage;
  const productAccess = useSelector(currentProductAccessSelector);
  const users = useSelector(allUsersSelector);
  const invites = useSelector(allInvitesSelector);
  const inviteUsersResult = useSelector(
    (state: RootState): Partial<AdminClientSelectors.SuccessesAndFailures> =>
      AdminClientSelectors.getSuccessesAndFailures(
        get(state, 'adminClient.adminInvites.productAccessInviteUsers.result') as
        | Record<string, unknown>
        | null
        | undefined,
      ) || {},
  );
  const openInviteModal = useSelector((state: RootState) =>
    get(state, 'adminClient.adminInvites.productAccessInviteUsers'),
  );

  const dispatch = useAppDispatch();
  const hideModal = (): void => {
    dispatch(CloudAdminActions.hideModal());
  };
  const inviteToProduct = (
    emailAddresses: string[],
    costCenterId: unknown,
    productAccessId: unknown,
  ): void => {
    dispatch(
      AdminClientActions.reqProductAccessInviteUsers(
        emailAddresses,
        costCenterId as string,
        productAccessId as string,
      ),
    );
  };
  const closeInviteUsersResults = (): void => {
    dispatch(AdminClientActions.clientProductAccessInviteUsersResultClear());
  };

  const [userEmails, setUserEmails] = useState('');
  const [errorMessageArray, setErrorMessageArray] = useState<React.ReactNode[]>([]);
  const [showValidationErrorDetails, setShowValidationErrorDetails] = useState(false);
  // Preserves the class forceUpdate() calls on textarea focus/keydown.
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  const getEmailsArray = (emailsStr: string = userEmails): string[] =>
    // `undefined` is stripped by `without`, so remaining items are strings.
    without((emailsStr || '').split(/[\n,]/), ' ', '', undefined).map((item) => item!.trim());

  const isValid = (): boolean => {
    // Regardless of product, presence of errors or absence of emails => disabled
    if (errorMessageArray.length || !getEmailsArray().length) {
      return false;
    }
    // FlowJo/SeqGeq-specific: need seats available
    return seats.openSeats > 0;
  };

  const handleAdd = (): void => {
    const costCenter = userAdminState.costCenter as CostCenter | undefined;
    const { id: costCenterId } = costCenter!;
    const emailAddresses = getEmailsArray();

    // Redundant safeguard, since button is disabled until valid
    if (!isValid()) {
      return;
    }

    inviteToProduct(emailAddresses, costCenterId, productAccess.id);
  };

  const validateEmails = (emailsStr: string): void => {
    const costCenter = userAdminState.costCenter as CostCenter | undefined;
    const { whitelistDomains, whitelistIgnore, id: costCenterId } = costCenter!;

    let locErrorMessageArray: React.ReactNode[] = [];
    const emailAddresses = getEmailsArray(emailsStr);

    // validate that there are whitelisted domains if this is FlowJo or SeqGeq
    if (!whitelistDomains) {
      locErrorMessageArray.push(
        <div key={`no-whitelisted-domains-for-${costCenterId}`}>
          No whitelisted domains for this institution. Please contact FlowJo for assistance.
        </div>,
      );
    }

    // validate that there are enough seats for the number of emails provided
    if (emailAddresses.length > seats.openSeats) {
      locErrorMessageArray.push(
        <div key="not-enough-seats">You do not have enough seats available</div>,
      );
    }

    // validate that at least one email address was added, regardless of product
    if (!emailAddresses.length) {
      locErrorMessageArray.push(
        <div key="no-email-found" style={{ margin: '0 auto' }}>
          Valid email address(es) required. Example: example@yourdomain.com
        </div>,
      );
    }

    emailAddresses.forEach((email) => {
      // trim email address of whitespace
      const emailAddress = email.trim();
      // create a flag we can toggle for whitelist comparison
      let doWhitelistComparison = true;

      // validate email address
      const emailPrevalidationForUserArray = validateEmail(emailAddress);

      // validate that this email does not already exist in the license (invite or user)
      const emailNotDupeUser = validateNoDuplicates(users, 'primaryEmail');
      if (!emailNotDupeUser.validator(emailAddress)) {
        locErrorMessageArray.push(
          <div key={`${emailAddress}-exists-in-license`}>
            {emailAddress} already exists in license.
          </div>,
        );
        doWhitelistComparison = false;
      }

      const emailNotDupeInvite = validateNoDuplicates(invites, 'emailAddress');
      if (!emailNotDupeInvite.validator(emailAddress)) {
        locErrorMessageArray.push(
          <div key={`${emailAddress}-exists-in-license`}>
            {emailAddress} has already been invited to this license.
          </div>,
        );
        doWhitelistComparison = false;
      }

      // if there are syntax errors in the email, don't do whitelist validation
      // just return syntax errors
      if (emailPrevalidationForUserArray.length) {
        locErrorMessageArray = locErrorMessageArray.concat(emailPrevalidationForUserArray);
        doWhitelistComparison = false;
      }

      if (whitelistIgnore) {
        doWhitelistComparison = false;
      }

      if (doWhitelistComparison) {
        const cc = { whitelistDomains, whitelistIgnore };
        const notOnWhitelistArray = !LicenseUtils.checkWhitelist(emailAddress, cc);
        // if the email does not have a whitelisted domain, flag it
        if (notOnWhitelistArray) {
          locErrorMessageArray.push(
            <div key={`${email}-not-on-whitelist`}>
              <span className="email-invalidated">[{emailAddress}]</span> Domain is not on
              whitelist.
            </div>,
          );
        }
      }
    });

    // store error messages in state
    setErrorMessageArray(locErrorMessageArray);
  };

  const handleCloseModal = (): void => {
    closeInviteUsersResults();
    // reset the state
    setUserEmails('');
    setErrorMessageArray([]);
    hideModal();
  };

  const showErrDetailsLink = (): boolean => !!errorMessageArray.length;

  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = event.target.value;
    setUserEmails(value);
    validateEmails(value);
  };

  const renderRemainingSeats = (): React.ReactElement => {
    const { openSeats } = seats;
    // A reduced maxActiveUsers/maxSharedComputers can leave a license over its seat quota
    // (openSeats negative) rather than merely at 0 - "You have -3 open seats available" reads as
    // a bug, so this gets its own message instead of falling through to the positive-count copy.
    if (openSeats < 0) {
      const overQuotaCount = Math.abs(openSeats);
      return (
        <AlertMessage noIcon>
          Your license is currently <strong>{overQuotaCount}</strong>{' '}
          {Utils.pluralize(overQuotaCount, 'seat')} over its quota. Contact FlowJo sales to discuss
          adding more seats to the license.
        </AlertMessage>
      );
    }
    return (
      <AlertMessage noIcon>
        You have <strong>{openSeats}</strong> open {Utils.pluralize(openSeats, 'seat')} available.
      </AlertMessage>
    );
  };

  const renderModalBeforeAdd = (): React.ReactElement => (
    <div className="new-user-modal">
      <Modal
        size={FjPrimitives.fjModalSizes.LG}
        onRequestClose={() => handleCloseModal()}
        cancel={{
          text: 'Cancel',
          callback: () => handleCloseModal(),
        }}
        accept={{
          text: 'Invite',
          disabled: !isValid(),
          callback: handleAdd,
        }}
        title="Invite New Users"
      >
        <span>
          Invited users will be assigned a seat on your site license. {renderRemainingSeats()}
        </span>
        <FjPrimitives.FjTextArea
          placeholder={getPlaceholder()}
          onChange={handleTextAreaChange}
          value={userEmails}
          // eslint complains about autofocus, but since we're in a focus trapped
          // modal here anyway, this is pretty safe to disable
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onFocus={forceRender}
          onKeyDown={forceRender}
          role="button"
          tabIndex={0}
        />
        <AlertMessage>
          {showErrDetailsLink() && errorMessageArray.length && errorMessageArray[0]}
        </AlertMessage>
      </Modal>
    </div>
  );

  const renderModalAfterAdd = ({
    successCount = 0,
    failures = [],
  }: {
    successCount?: number;
    failures?: Array<[string, unknown]>;
  } = {}): React.ReactElement => {
    const failuresCount = failures.length;
    const hasFailures = !!failuresCount;
    const productAccessErrorCount = failures.filter(
      (f) => f[1] === CloudErrors.inviteUserErrorTypes.USER_HAS_EXISTING_PRODUCT_ACCESS,
    ).length;
    const otherErrorCount = failuresCount - productAccessErrorCount;

    const failuresText = `${failuresCount} ${Utils.pluralize(
      failuresCount,
      'invite was',
      'invites were',
    )} not
        sent.`;

    return (
      <Modal
        accept={{
          text: 'OK',
          callback: handleCloseModal,
        }}
        title={[
          `Successfully invited ${successCount} ${Utils.pluralize(successCount, 'user')}`,
          <br />,
          <AlertMessage noIcon>{failuresText}</AlertMessage>,
        ]}
        centerText
      >
        {hasFailures && (
          <FjPrimitives.FjFlexContainer direction="column">
            <InviteFailuresList value={failures.join('\n')} />
            <AlertMessage centered>
              {productAccessErrorCount > 0 && [
                `${productAccessErrorCount} invited ${Utils.pluralize(
                  productAccessErrorCount,
                  'user is',
                  'users are',
                )} already in this license.`,
                <br />,
              ]}
              {otherErrorCount > 0 &&
                `${otherErrorCount} ${Utils.pluralize(
                  otherErrorCount,
                  'invite',
                )} failed. Please verify email
              ${Utils.pluralize(otherErrorCount, 'address', 'addresses')} and try again.`}
            </AlertMessage>
          </FjPrimitives.FjFlexContainer>
        )}
      </Modal>
    );
  };

  const renderPrimaryModal = (): React.ReactElement | null => {
    const { costCenter } = userAdminState;

    if (!costCenter) {
      return null;
      // this next case covers both FlowJo and SeqGeq
    }
    if (inviteUsersResult) {
      const { successes, failures } = inviteUsersResult;
      if (openInviteModal && (successes || failures)) {
        return renderModalAfterAdd({
          successCount: successes ? successes.length : 0,
          failures: failures ? flatten(failures.map((failure) => Object.entries(failure))) : [],
        });
      }
    }

    return renderModalBeforeAdd();
  };

  const renderErrorDetailsModal = (): React.ReactElement | null => {
    if (!showValidationErrorDetails) {
      return null;
    }

    return (
      <Modal
        size={FjPrimitives.fjModalSizes.LG}
        contentClassName="stacked-modal-1"
        accept={{
          callback: () => setShowValidationErrorDetails(false),
          text: 'OK',
        }}
      >
        <h3 className="margin-top-sm margin-bottom-1 fj-header">Error Details</h3>
        {errorMessageArray}
      </Modal>
    );
  };

  return (
    <div>
      {renderErrorDetailsModal()}
      {renderPrimaryModal()}
    </div>
  );
};

export default NewUsers;
